<section class="billing-health-card card" aria-labelledby="billing-health-title">
  <header class="billing-health-header">
    <div>
      <div class="eyebrow">Superadmin</div>
      <h2 id="billing-health-title">Billing health</h2>
      <p>Track customers expected to be billed, including accounts with no recorded attempt.</p>
    </div>
    <a class="payment-history-button" mat-flat-button routerLink="/settings/history">
      <mat-icon aria-hidden="true">receipt_long</mat-icon>
      <span>View payment attempts</span>
    </a>
  </header>

  <div class="summary-grid" aria-label="Billing summary">
    <button
      type="button"
      class="summary-card summary-success"
      [class.summary-selected]="selectedStatus === billingHealthStatus.SuccessfullyBilled"
      [attr.aria-pressed]="selectedStatus === billingHealthStatus.SuccessfullyBilled"
      (click)="selectStatus(billingHealthStatus.SuccessfullyBilled)"
    >
      <span class="summary-value">{{ summary.successfullyBilled }}</span>
      <span class="summary-label">Successfully billed</span>
    </button>
    <button
      type="button"
      class="summary-card summary-failed"
      [class.summary-selected]="selectedStatus === billingHealthStatus.Failed"
      [attr.aria-pressed]="selectedStatus === billingHealthStatus.Failed"
      (click)="selectStatus(billingHealthStatus.Failed)"
    >
      <span class="summary-value">{{ summary.failed }}</span>
      <span class="summary-label">Failed</span>
    </button>
    <button
      type="button"
      class="summary-card summary-overdue"
      [class.summary-selected]="selectedStatus === billingHealthStatus.Overdue"
      [attr.aria-pressed]="selectedStatus === billingHealthStatus.Overdue"
      (click)="selectStatus(billingHealthStatus.Overdue)"
    >
      <span class="summary-value">{{ summary.overdue }}</span>
      <span class="summary-label">Overdue — no attempt</span>
    </button>
    <button
      type="button"
      class="summary-card summary-due"
      [class.summary-selected]="selectedStatus === billingHealthStatus.DueLater"
      [attr.aria-pressed]="selectedStatus === billingHealthStatus.DueLater"
      (click)="selectStatus(billingHealthStatus.DueLater)"
    >
      <span class="summary-value">{{ summary.dueLater }}</span>
      <span class="summary-label">Due later this month</span>
    </button>
    <button
      type="button"
      class="summary-card summary-excluded"
      [class.summary-selected]="selectedStatus === billingHealthStatus.Excluded"
      [attr.aria-pressed]="selectedStatus === billingHealthStatus.Excluded"
      (click)="selectStatus(billingHealthStatus.Excluded)"
    >
      <span class="summary-value">{{ summary.excluded }}</span>
      <span class="summary-label">Excluded / not due</span>
    </button>
  </div>

  <section class="filters-panel" aria-labelledby="billing-filters-title">
    <div class="filters-heading">
      <span class="filters-heading-icon" aria-hidden="true">
        <mat-icon>tune</mat-icon>
      </span>
      <div>
        <h3 id="billing-filters-title">Find billing records</h3>
        <p>Choose a month, then narrow the customer list by name or billing status.</p>
      </div>
    </div>

    <div class="filters-row">
      <label class="month-filter">
        <span class="control-label">Billing month</span>
        <span class="month-input-shell">
          <mat-icon aria-hidden="true">calendar_month</mat-icon>
          <input
            type="month"
            aria-label="Billing month"
            [(ngModel)]="selectedMonth"
            [max]="maxMonth"
            (change)="onFiltersChanged()"
          />
        </span>
      </label>

      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="search-filter">
        <mat-label>Search customers</mat-label>
        <input
          matInput
          [(ngModel)]="search"
          (ngModelChange)="onSearchChanged($event)"
          placeholder="Organization, username, or email"
        />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="status-filter">
        <mat-label>Billing status</mat-label>
        <mat-select [(ngModel)]="selectedStatus" (selectionChange)="onFiltersChanged()">
          <mat-option [value]="null">All statuses</mat-option>
          <mat-option [value]="billingHealthStatus.SuccessfullyBilled">Successfully billed</mat-option>
          <mat-option [value]="billingHealthStatus.Failed">Failed</mat-option>
          <mat-option [value]="billingHealthStatus.Overdue">Overdue — no attempt</mat-option>
          <mat-option [value]="billingHealthStatus.DueLater">Due later</mat-option>
          <mat-option [value]="billingHealthStatus.Excluded">Excluded / not due</mat-option>
        </mat-select>
      </mat-form-field>

      <button class="reset-filters-button" mat-stroked-button type="button" (click)="clearFilters()">
        <mat-icon aria-hidden="true">restart_alt</mat-icon>
        <span>Reset filters</span>
      </button>
    </div>
  </section>

  <div *ngIf="isLoading" class="loading-state">
    <mat-spinner diameter="36"></mat-spinner>
    <span>Checking billing health…</span>
  </div>

  <div *ngIf="!isLoading" class="table-wrapper">
    <table mat-table [dataSource]="dataSource" aria-label="Customer billing health">
      <ng-container matColumnDef="customer">
        <th mat-header-cell *matHeaderCellDef>Customer</th>
        <td mat-cell *matCellDef="let customer">
          <strong>{{ customer.organizationName }}</strong>
          <span class="secondary-text">{{ customer.ownerUsername }} · {{ customer.ownerEmail }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="billingStatus">
        <th mat-header-cell *matHeaderCellDef>Billing status</th>
        <td mat-cell *matCellDef="let customer">
          <span
            class="status-badge"
            [class.status-success]="customer.billingHealthStatus === billingHealthStatus.SuccessfullyBilled"
            [class.status-failed]="customer.billingHealthStatus === billingHealthStatus.Failed"
            [class.status-overdue]="customer.billingHealthStatus === billingHealthStatus.Overdue"
            [class.status-due]="customer.billingHealthStatus === billingHealthStatus.DueLater"
            [class.status-excluded]="customer.billingHealthStatus === billingHealthStatus.Excluded"
          >
            {{ getStatusLabel(customer.billingHealthStatus) }}
          </span>
          <span *ngIf="customer.exclusionReason" class="secondary-text">
            {{ customer.exclusionReason }}
          </span>
          <span *ngIf="customer.retryCount" class="secondary-text">
            {{ customer.retryCount }} failed {{ customer.retryCount === 1 ? "attempt" : "attempts" }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="subscription">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let customer">
          <strong>{{ customer.subscriptionName }}</strong>
          <span class="secondary-text">
            {{ getBillingPeriodLabel(customer.billingPeriod) }} · {{ customer.price | currency:"USD":"symbol":"1.2-2" }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="nextBillingDate">
        <th mat-header-cell *matHeaderCellDef>Expected billing</th>
        <td mat-cell *matCellDef="let customer">
          {{ customer.nextBillingDate | date:"MMM d, y" }}
          <span *ngIf="customer.lastBilled" class="secondary-text">
            Paid-through date: {{ customer.lastBilled | date:"MMM d, y" }}
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="lastSuccessfulCharge">
        <th mat-header-cell *matHeaderCellDef>Last successful charge</th>
        <td mat-cell *matCellDef="let customer">
          <ng-container *ngIf="customer.lastSuccessfulChargeAt; else noSuccessfulCharge">
            {{ customer.lastSuccessfulChargeAt | date:"MMM d, y, h:mm a" }}
          </ng-container>
          <ng-template #noSuccessfulCharge>—</ng-template>
        </td>
      </ng-container>

      <ng-container matColumnDef="latestAttempt">
        <th mat-header-cell *matHeaderCellDef>Latest attempt</th>
        <td mat-cell *matCellDef="let customer">
          <ng-container *ngIf="customer.latestAttemptAt; else noAttempt">
            <span [class.attempt-failed]="customer.latestAttemptSucceeded === false">
              {{ customer.latestAttemptSucceeded ? "Succeeded" : "Failed" }}
            </span>
            <span class="secondary-text">
              {{ customer.latestAttemptAt | date:"MMM d, y, h:mm a" }}
            </span>
            <span *ngIf="customer.latestAttemptSucceeded === false" class="secondary-text attempt-failed">
              {{ getFailureReason(customer.latestFailureReason) }}
            </span>
          </ng-container>
          <ng-template #noAttempt>—</ng-template>
        </td>
      </ng-container>

      <ng-container matColumnDef="paymentMethod">
        <th mat-header-cell *matHeaderCellDef>Payment method</th>
        <td mat-cell *matCellDef="let customer">
          <ng-container *ngIf="customer.paysOutsideApp">Outside app</ng-container>
          <ng-container *ngIf="!customer.paysOutsideApp && customer.hasPaymentMethod">
            Card •••• {{ customer.creditCardLast4 || "—" }}
          </ng-container>
          <span *ngIf="!customer.paysOutsideApp && !customer.hasPaymentMethod" class="attempt-failed">
            Missing
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Action</th>
        <td mat-cell *matCellDef="let customer">
          <button
            *ngIf="canRetry(customer)"
            class="retry-payment-button"
            mat-stroked-button
            type="button"
            [disabled]="retryingSubscriptionId === customer.organizationSubscriptionId"
            (click)="openRetryPaymentDialog(customer)"
          >
            <mat-icon
              aria-hidden="true"
              [class.retry-icon-spinning]="retryingSubscriptionId === customer.organizationSubscriptionId"
            >
              {{ retryingSubscriptionId === customer.organizationSubscriptionId ? "sync" : "refresh" }}
            </mat-icon>
            <span>
              {{ retryingSubscriptionId === customer.organizationSubscriptionId ? "Retrying…" : "Retry payment" }}
            </span>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell empty-state" [attr.colspan]="displayedColumns.length">
          No customers match these billing filters.
        </td>
      </tr>
    </table>
  </div>

  <mat-paginator
    *ngIf="!isLoading"
    [length]="totalCount"
    [pageIndex]="pageIndex"
    [pageSize]="pageSize"
    [pageSizeOptions]="[10, 20, 50]"
    showFirstLastButtons
    (page)="pageChanged($event)"
  ></mat-paginator>
</section>
